import { AfterViewChecked, Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { faAutomobile, faBuilding, faPaperPlane, faUser } from '@fortawesome/free-solid-svg-icons';
import { Subject, Subscription, interval } from 'rxjs';
import { last, takeUntil } from "rxjs/operators";
import { FileTypeConstants } from '../../../shared/constants/file-upload-type-constants';
import { DialogComponent } from '../../../shared/dialog/dialog.component';
import { FileUploadComponent } from '../../../shared/file-upload/file-upload.component';
import { UserCache } from "../services/user/user.cache";
import { ClaimService } from '../../claims/claim.service';
import { ClaimsAppCustomerComponent } from '../claims-app-customer/claims-app-customer.component';
import { ClaimsAppMechanicalBreakdownComponent } from '../claims-app-mechanical-breakdown/claims-app-mechanical-breakdown.component';
import { ConcernCauseComponentListComponent } from '../concern-cause-component-list/concern-cause-component-list.component';
import { ClaimsAppSubmissionMapper } from '../mappers/claims-app-submission.mapper';
import { ClaimsAppProgressStepComponent, ProgressStep } from '../claims-app-progress-step/claims-app-progress-step.component';
import { ClaimsAppRequest } from '../models/claims-app-request';
import { IRepairFacility } from '../services/user/models/repair-facility';
import { UserContact } from '../services/user/models/user-contact';
import { UserManager } from '../services/user/user.manager';
import { environment } from '../../../environments/environment';
import { EstimateTotal } from '../models/estimates/estimate-total';
import { FormValidationTrackerService, InvalidField } from '../services/form-service/form-validation-tracker.service';
import { LaborRateService } from '../estimates/labor-estimate/labor-rate.service';
import { EstimateTotalService } from '../services/estimate-total.service';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons';

@Component({
    selector: 'claims-app-submission',
    templateUrl: './claims-app-submission.component.html',
    styleUrl: './claims-app-submission.component.scss'
})
export class ClaimsAppSubmissionComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    @ViewChild('customer') customer: ClaimsAppCustomerComponent;
    @ViewChild('mechanicalBreakdown') mechanicalBreakdown: ClaimsAppMechanicalBreakdownComponent;
    @ViewChild('concernCausePartList') concernCausePartList: ConcernCauseComponentListComponent;
    @ViewChild('estimateAttachment') estimateAttachment: FileUploadComponent;
    @ViewChild('formScrollableContent') formScrollabeContent: Element;
    @ViewChild('progressFloat') progressFloat: ClaimsAppProgressStepComponent;

    public faCheckCircle = faCheckCircle;
    public enableEstimateUi: boolean = environment.enableEstimateUi == "true";

    public repairFacility: IRepairFacility;
    public contact: UserContact;
    private readonly _destroying$ = new Subject<void>();

    public acceptedFiles: string[] = [FileTypeConstants.txt,
    FileTypeConstants.doc,
    FileTypeConstants.docx,
    FileTypeConstants.pdf,
    FileTypeConstants.jpeg,
    FileTypeConstants.jpg,
    FileTypeConstants.png,
    FileTypeConstants.PNG,
    FileTypeConstants.gif];

    public faSolidUser = faUser;
    public faSolidCar = faAutomobile
    public faSolidBuilding = faBuilding;
    public faSolidPaperPlane = faPaperPlane;

    public claimServiceResponseMessage: string;

    public modelDialog: MatDialogRef<DialogComponent>;

    // Indicates if the form has been submitted.
    public isFormSubmitted = false;
    private isSubmitting = false;
    public shouldScrollToTop = false;

    public claimServiceSubscription: Subscription;

    private readonly defaultSuccessMessage: string = 'Thank you for your submission. We will contact you soon to finalize your claim.';

    public progressSteps: ProgressStep[] = [];
    public activeStepIndex = 1;

    // Track if sections have been validated for errors
    public customerValidated = false;
    private breakdownValidated = false;
    public customerSubmissionClicked = false; // Track if the customer section's submission button has been clicked at least once

    // Invalid fields tracking
    public invalidFields: InvalidField[] = [];

    // Public properties for template binding (replacing getters)
    public estimateTotal: EstimateTotal = new EstimateTotal(0, 0, 0, 0, 0);
    public isLoading: boolean = false;

    constructor(private claimService: ClaimService,
        private userManager: UserManager,
        private userCache: UserCache,
        private dialog: MatDialog,
        private changeDetectorRef: ChangeDetectorRef,
        private formValidationTracker: FormValidationTrackerService,
        private laborRateService: LaborRateService,
        private estimateTotalService: EstimateTotalService) {
    }


    ngOnInit(): void {
        // Initialize property values
        this.updateIsLoading();
        this.updateEstimateTotal();

        this.userCache.updated
            .pipe(takeUntil(this._destroying$))
            .subscribe(() => {
                this.repairFacility = this.userCache.selectedRepairFacility;
                this.contact = this.userCache.selectedContact;
                this.updateProgress();
                this.updateIsLoading(); // Update loading state when userManager changes
            });

        // Subscribe to invalid fields changes
        this.formValidationTracker.invalidFields$
            .pipe(takeUntil(this._destroying$))
            .subscribe(invalidFields => {
                this.invalidFields = invalidFields;
                // Use markForCheck instead of detectChanges to avoid ExpressionChangedAfterItHasBeenCheckedError
                this.changeDetectorRef.markForCheck();
            });

        // Set up periodic polling to check for loading state changes
        // This is needed because userManager.updatingRepairFacilities changes asynchronously
        interval(100)
            .pipe(takeUntil(this._destroying$))
            .subscribe(() => {
                const previousLoading = this.isLoading;
                this.updateIsLoading();
                // Only trigger change detection if the loading state actually changed
                if (previousLoading !== this.isLoading) {
                    this.changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Update the isLoading property when dependent values change.
     */
    private updateIsLoading(): void {
        this.isLoading = this.userManager.updatingRepairFacilities || this.isSubmitting;
    }

    /**
     * Update the estimateTotal property when dependent values change.
     */
    private updateEstimateTotal(): void {
        // Use the reactive service instead of the old getter approach
        this.estimateTotal = this.estimateTotalService.getCurrentEstimateTotal();
    }

    ngAfterViewChecked(): void {
        // Scroll back to the top when resetting once the div is unhidden.
        if (this.shouldScrollToTop) {
            this.scrollToFirstSection();
            this.shouldScrollToTop = false;
        }
    }

    ngOnDestroy(): void {
        if (this.claimServiceSubscription) {
            this.claimServiceSubscription.unsubscribe();
        }

        // Clear subscribed forms tracking
        this.subscribedForms.clear();

        this._destroying$.next();
        this._destroying$.complete();
    }

    ngAfterViewInit(): void {
        // Use the centralized setup method
        this.setupFormSubscriptions();
    }

    /**
     * Subscribe to value changes for all concern cause part forms and estimate line items.
     */
    private subscribeToObjectFormsChanges(): void {
        try {
            if (this.concernCausePartList?.objectForms) {
                this.concernCausePartList.objectForms.forEach((concernForm, index) => {
                    // Subscribe to concern form changes
                    if (concernForm?.form && concernForm.form.valid !== undefined) {
                        concernForm.form.valueChanges
                            .pipe(takeUntil(this._destroying$))
                            .subscribe(() => {
                                setTimeout(() => {
                                    this.updateProgress();
                                    this.updateValidationState();
                                    this.changeDetectorRef.markForCheck();
                                }, 0);
                            });
                    }

                    // Subscribe to estimate line item form changes
                    this.subscribeToEstimateFormChanges(concernForm);
                });
            }
        } catch (error) {
            console.warn('Error subscribing to object forms changes:', error);
        }
    }

    // Track subscribed forms to avoid duplicate subscriptions
    private subscribedForms = new Set<any>();

    /**
     * Subscribe to estimate line item form changes for real-time validation updates
     */
    private subscribeToEstimateFormChanges(concernForm: any): void {
        try {
            if (concernForm?.correctionEstimateList?.objectForms) {
                concernForm.correctionEstimateList.objectForms.forEach((correction: any) => {
                    const estimateList = correction.estimateList;

                    // Subscribe to part estimate form changes
                    if (estimateList?.partForms) {
                        estimateList.partForms.forEach((partForm: any) => {
                            if (partForm?.form && !this.subscribedForms.has(partForm.form)) {
                                this.subscribedForms.add(partForm.form);
                                partForm.form.valueChanges
                                    .pipe(takeUntil(this._destroying$))
                                    .subscribe(() => {
                                        setTimeout(() => {
                                            this.updateValidationState();
                                            this.updateEstimateTotal(); // Update estimate total when estimate forms change
                                            this.changeDetectorRef.markForCheck();
                                        }, 0);
                                    });
                            }
                        });

                        // Monitor for newly added part forms by subscribing to changes in the QueryList
                        if (estimateList.partForms.changes && !this.subscribedForms.has(estimateList.partForms)) {
                            this.subscribedForms.add(estimateList.partForms);
                            estimateList.partForms.changes
                                .pipe(takeUntil(this._destroying$))
                                .subscribe(() => {
                                    setTimeout(() => {
                                        // Re-subscribe to any newly added forms
                                        this.subscribeToEstimateFormChanges(concernForm);
                                    }, 100);
                                });
                        }
                    }

                    // Subscribe to labor estimate form changes
                    if (estimateList?.laborForms) {
                        estimateList.laborForms.forEach((laborForm: any) => {
                            if (laborForm?.form && !this.subscribedForms.has(laborForm.form)) {
                                this.subscribedForms.add(laborForm.form);
                                laborForm.form.valueChanges
                                    .pipe(takeUntil(this._destroying$))
                                    .subscribe(() => {
                                        setTimeout(() => {
                                            this.updateValidationState();
                                            this.updateEstimateTotal(); // Update estimate total when estimate forms change
                                            this.changeDetectorRef.markForCheck();
                                        }, 0);
                                    });
                            }
                        });

                        // Monitor for newly added labor forms
                        if (estimateList.laborForms.changes && !this.subscribedForms.has(estimateList.laborForms)) {
                            this.subscribedForms.add(estimateList.laborForms);
                            estimateList.laborForms.changes
                                .pipe(takeUntil(this._destroying$))
                                .subscribe(() => {
                                    setTimeout(() => {
                                        this.subscribeToEstimateFormChanges(concernForm);
                                    }, 100);
                                });
                        }
                    }

                    // Subscribe to material estimate form changes
                    if (estimateList?.materialForms) {
                        estimateList.materialForms.forEach((materialForm: any) => {
                            if (materialForm?.form && !this.subscribedForms.has(materialForm.form)) {
                                this.subscribedForms.add(materialForm.form);
                                materialForm.form.valueChanges
                                    .pipe(takeUntil(this._destroying$))
                                    .subscribe(() => {
                                        setTimeout(() => {
                                            this.updateValidationState();
                                            this.updateEstimateTotal(); // Update estimate total when estimate forms change
                                            this.changeDetectorRef.markForCheck();
                                        }, 0);
                                    });
                            }
                        });

                        // Monitor for newly added material forms
                        if (estimateList.materialForms.changes && !this.subscribedForms.has(estimateList.materialForms)) {
                            this.subscribedForms.add(estimateList.materialForms);
                            estimateList.materialForms.changes
                                .pipe(takeUntil(this._destroying$))
                                .subscribe(() => {
                                    setTimeout(() => {
                                        this.subscribeToEstimateFormChanges(concernForm);
                                    }, 100);
                                });
                        }
                    }
                });
            }
        } catch (error) {
            console.warn('Error subscribing to estimate form changes:', error);
        }
    }

    /**
     * Update validation state dynamically as user fills forms.
     */
    private updateValidationState(): void {        
        // Only update if validation has been attempted        
        if (!this.customerValidated || !this.breakdownValidated) {
            return;
        }

        const invalidFields: string[] = [];

        // Check customer form
        if (this.customer?.form?.controls && this.customerValidated) {
            Object.keys(this.customer.form.controls).forEach(key => {
                if (this.customer.form.get(key)?.invalid) {
                    invalidFields.push(key);
                }
            });
        }

        // Check mechanical breakdown form
        if (this.mechanicalBreakdown?.form?.controls && this.breakdownValidated) {
            Object.keys(this.mechanicalBreakdown.form.controls).forEach(key => {
                if (this.mechanicalBreakdown.form.get(key)?.invalid) {
                    invalidFields.push(key);
                }
            });
        }

        // Check concern cause part list forms (only if validation has been attempted)
        if (this.concernCausePartList?.objectForms && (this.customerValidated || this.breakdownValidated)) {
            this.concernCausePartList.objectForms.forEach(concernForm => {
                if (concernForm?.form) {
                    Object.keys(concernForm.form.controls).forEach(key => {
                        const control = concernForm.form.get(key);
                        if (control?.invalid) {
                            invalidFields.push(key);
                        }
                    });
                }
            });
        }

        // Include estimate line item validation if validation has been attempted
        if (this.customerValidated || this.breakdownValidated) {
            this.validateEstimateLineItems(invalidFields);
        }

        // Update validation tracker
        this.formValidationTracker.updateInvalidFields(invalidFields);
    }

    /**
     * Navigate to selected section.
     */
    public scrollToSection(sectionName: string) {
        const section = document.getElementById(sectionName);
        section.scrollIntoView({ behavior: 'smooth' });        
    }

    /**
     * Navigate to selected first found section.
     */
    public scrollToFirstSection() {
        const firstSection = document.querySelector('section');
        firstSection.scrollIntoView({ behavior: 'smooth' });
    }

    /**
     * Scroll to the validation warning banner.
     */
    public scrollToValidationBanner() {
        const banner = document.getElementById('validation-warning-banner');
        if (banner) {
            banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    /**
     * Get formatted text of invalid fields for display.
     * @deprecated Now using bulleted list format in template
     */
    public getInvalidFieldsText(): string {
        if (this.invalidFields.length === 0) return '';

        const fieldNames = this.invalidFields.map(field => field.displayName);

        if (fieldNames.length === 1) {
            return fieldNames[0];
        } else if (fieldNames.length === 2) {
            return fieldNames.join(' and ');
        } else {
            const lastField = fieldNames.pop();
            return fieldNames.join(', ') + ', and ' + lastField;
        }
    }

    /**
     * Confirm with the user that no estimated was attached.
     */
    private confirmEstimateUpload(): void {
        this.showDialog('Estimate confirmation',
            'Please note we need cause of failure and a repair estimate to authorize a claim. Are you sure you want to continue without uploading the estimate?',
            'Cancel',
            'Yes, Continue',
            () => this.submit(),
            () => { });
    }

    /**
     * Confirm with the user when no estimate method has been provided.
     */
    private confirmNoEstimate(): void {
        this.showDialog('Repair Estimate Required',
            'A repair estimate is required to approve a claim. Are you sure you want to continue without providing an estimate?',
            'Cancel',
            'Yes, Continue',
            () => this.submit(),
            () => { });
    }

    /**
     * Show confirmation dialog of provided message.
     */
    public showDialog(title: string,
        messageText: string,
        cancelButtonText: string,
        confirmationButtonText: string,
        onConfirmEvent: () => void,
        onCancelEvent: () => void): void {
        this.modelDialog = this.dialog.open(DialogComponent, {
            data: {
                title: title,
                bodyText: messageText,
                cancelButtonText: cancelButtonText,
                confirmationButtonText: confirmationButtonText
            }
        });
        this.modelDialog.afterClosed().subscribe(result => {
            if (result) onConfirmEvent();
            else onCancelEvent();
        });
    }

    /**
     * On submission of form.
     */
    public onSubmit(): void {
        // First, validate all required fields and mark them as invalid if needed
        const allRequiredFieldsValid = this.validateAllRequiredData();

        // Only proceed with estimate validation if all required fields are valid
        if (!allRequiredFieldsValid) {
            // Required fields are invalid - stop here and let user see validation errors
            return;
        }

        // All required fields are valid, now check estimate validation state
        // Force any pending form updates to be processed before checking estimate totals
        // This is crucial when submitting without losing focus from the last input field
        if (this.concernCausePartList) {
            // Trigger recalculation of totals from the concern cause component list
            this.concernCausePartList.triggerRecalculation();
        }

        // Ensure we have the latest estimate total before checking
        this.updateEstimateTotal();

        const hasUploadedEstimate = this.estimateAttachment?.importFiles.length > 0;
        const hasEstimateTotal = this.hasEstimateTotal();
        const hasValidLineItems = this.areEstimateLineItemsValid();

        // Debug logging to help troubleshoot the issue
        console.log('=== ESTIMATE SUBMISSION DEBUG ===');
        console.log('estimateTotal object:', this.estimateTotal);
        console.log('hasUploadedEstimate:', hasUploadedEstimate);
        console.log('hasEstimateTotal:', hasEstimateTotal);
        console.log('hasValidLineItems:', hasValidLineItems);
        console.log('estimateTotal.laborTotal:', this.estimateTotal.laborTotal);
        console.log('estimateTotal.laborTaxTotal:', this.estimateTotal.laborTaxTotal);
        console.log('estimateTotal.partTotal:', this.estimateTotal.partTotal);
        console.log('estimateTotal.partTaxTotal:', this.estimateTotal.partTaxTotal);
        console.log('estimateTotal.supplyTotal:', this.estimateTotal.supplyTotal);
        console.log('estimateTotal.supplyTaxTotal:', this.estimateTotal.supplyTaxTotal);

        // If there's an estimate total but line items are invalid, stop here
        if (hasEstimateTotal && !hasValidLineItems) {
            // Estimate exists but has validation errors - user needs to fix them first
            console.log('Stopping submission: hasEstimateTotal=true but hasValidLineItems=false');
            return;
        }

        // Create estimate state for switch logic
        // Second parameter should be true if we have valid estimate line items (regardless of total amount)
        const hasValidEstimateLineItems = hasEstimateTotal && hasValidLineItems;
        const estimateState = this.getEstimateState(hasUploadedEstimate, hasValidEstimateLineItems);

        console.log('hasValidEstimateLineItems:', hasValidEstimateLineItems);
        console.log('estimateState:', estimateState);
        console.log('===========================');

        switch (estimateState) {
            case 'BOTH_PROVIDED':
            case 'UPLOAD_ONLY':
            case 'CONCERN_ONLY':
                // At least one estimate method provided - submit the form
                this.submit();
                break;

            case 'NONE_PROVIDED':
            default:
                // No estimate method provided - show required estimate dialog
                this.confirmNoEstimate();
                break;
        }
    }

    /**
     * Determines the current estimate state based on uploaded files and customer concerns.
     */
    private getEstimateState(hasUploadedEstimate: boolean, hasCustomerConcern: boolean): string {
        if (hasUploadedEstimate && hasCustomerConcern) {
            return 'BOTH_PROVIDED';
        } else if (hasUploadedEstimate) {
            return 'UPLOAD_ONLY';
        } else if (hasCustomerConcern) {
            return 'CONCERN_ONLY';
        } else {
            return 'NONE_PROVIDED';
        }
    }

    /**
     * Submit claim to destination.
     */
    private submit(): void {
        const estimateFile: File = this.estimateAttachment.importFiles[0] ?? null;
        const request = this.buildRequest();
        this.saveClaim(request, estimateFile);
    }


    private validateForm(invalidFields: string[], section: ClaimsAppCustomerComponent | ClaimsAppMechanicalBreakdownComponent): boolean {        
        section.validateForm();

        // Check form
        if (section?.form?.controls) {
            Object.keys(section.form.controls).forEach(key => {
                if (section.form.get(key)?.invalid) {
                    invalidFields.push(key);
                }
            });
        }        
        return section.formCompleted;
    }

    /**
   * Validate all forms in this component, including estimate line items.
   */
    private validateAllRequiredData(): boolean {
        // Collect invalid fields
        const invalidFields: string[] = [];
        
        //Check customerForm
        if (this.customer) {
            this.customerValidated = this.validateForm(invalidFields, this.customer);

            const firstName = this.getInvalidFieldIndex(invalidFields, 'customerFirstName');
            const lastName = this.getInvalidFieldIndex(invalidFields, 'customerLastName');

            if (firstName >= 0 || lastName >= 0) {
                const contractNumber = this.getInvalidFieldIndex(invalidFields, 'contractNumber');
                if (contractNumber >= 0) {
                    invalidFields.push('customerNameORcontractNumber');
                }
                else {
                    invalidFields.push('customerName');
                }
            }

        }
        this.mechanicalBreakdown?.validateForm();
        this.concernCausePartList?.validateForm();

        // Set validation tracking flags to show error states in UI
        this.breakdownValidated = true;
        // Check mechanical breakdown form
        if (this.mechanicalBreakdown) {
            this.validateForm(invalidFields, this.mechanicalBreakdown)
        }

        // Check concern cause part list forms
        if (this.concernCausePartList?.objectForms) {
            this.concernCausePartList.objectForms.forEach(concernForm => {
                if (concernForm.form) {
                    Object.keys(concernForm.form.controls).forEach(key => {
                        if (concernForm.form.get(key)?.invalid) {
                            invalidFields.push(key);
                        }
                    });
                }
            });
        }

        // Validate and collect estimate line item errors
        this.validateEstimateLineItems(invalidFields);

        // Update validation tracker
        this.formValidationTracker.updateInvalidFields(invalidFields);

        // Scroll to banner if there are errors
        if (invalidFields.length > 0) {
            setTimeout(() => this.scrollToValidationBanner(), 100);
        }

        this.updateProgress();

        const basicFormsValid = this.customerValidated
            && this.mechanicalBreakdown?.formCompleted
            && this.concernCausePartList?.formCompleted;

        const estimateLineItemsValid = this.areEstimateLineItemsValid();

        // Clear invalid fields if form is now valid
        if (basicFormsValid && estimateLineItemsValid && invalidFields.length === 0) {
            this.formValidationTracker.clearInvalidFields();
        }

        return basicFormsValid && estimateLineItemsValid;
    }

    private getInvalidFieldIndex(invalidFields: string[], fieldName: string) {
        const index = invalidFields.findIndex(x => x === fieldName);
        if (index >= 0) {
            invalidFields.splice(index, 1);
        }
        return index;
    }

    /**
     * Check if there is any estimate total (regardless of validation state).
     */
    private hasEstimateTotal(): boolean {
        const estimate = this.estimateTotal;
        const totalWithTax = (estimate.laborTotal + estimate.laborTaxTotal) +
            (estimate.partTotal + estimate.partTaxTotal) +
            (estimate.supplyTotal + estimate.supplyTaxTotal);

        return totalWithTax > 0;
    }

    /**
     * Check if there are valid customer concerns with valid line items.
     * Both the total estimate and line item validation must pass.
     * @deprecated Use hasEstimateTotal() and areEstimateLineItemsValid() separately for better logic separation
     */
    private hasValidCustomerConcern(): boolean {
        const estimate = this.estimateTotal;
        const totalWithTax = (estimate.laborTotal + estimate.laborTaxTotal) +
            (estimate.partTotal + estimate.partTaxTotal) +
            (estimate.supplyTotal + estimate.supplyTaxTotal);

        // Check if there are any estimate line items with validation errors
        const hasValidLineItems = this.areEstimateLineItemsValid();

        // Both total > 0 AND all line items must be valid
        return totalWithTax > 0 && hasValidLineItems;
    }

    /**
     * Check if all estimate line items across all customer concerns are valid.
     */
    private areEstimateLineItemsValid(): boolean {
        let allLineItemsValid = true;

        if (this.concernCausePartList?.objectForms) {
            this.concernCausePartList.objectForms.forEach(concernForm => {
                if (concernForm?.correctionEstimateList?.objectForms) {
                    concernForm.correctionEstimateList.objectForms.forEach(correction => {
                        const estimateList = correction.estimateList;

                        // Check part estimates
                        estimateList.partForms?.forEach(partForm => {
                            if (partForm?.form && !partForm.form.valid) {
                                allLineItemsValid = false;
                            }
                        });

                        // Check labor estimates
                        estimateList.laborForms?.forEach(laborForm => {
                            if (laborForm?.form && !laborForm.form.valid) {
                                allLineItemsValid = false;
                            }
                        });

                        // Check material estimates
                        estimateList.materialForms?.forEach(materialForm => {
                            if (materialForm?.form && !materialForm.form.valid) {
                                allLineItemsValid = false;
                            }
                        });
                    });
                }
            });
        }

        return allLineItemsValid;
    }

    /**
     * Validate estimate line items and add invalid fields to the provided array.
     * @param invalidFields Array to add invalid field names to
     */
    private validateEstimateLineItems(invalidFields: string[]): void {
        if (this.concernCausePartList?.objectForms) {
            this.concernCausePartList.objectForms.forEach((concernForm, concernIndex) => {
                // Get the customer concern text for this concern form
                const customerConcern = this.getCustomerConcernText(concernForm, concernIndex);

                if (concernForm?.correctionEstimateList?.objectForms) {
                    concernForm.correctionEstimateList.objectForms.forEach((correction, correctionIndex) => {
                        const estimateList = correction.estimateList;

                        // Check part estimates
                        estimateList.partForms?.forEach((partForm, partIndex) => {
                            if (partForm?.form) {
                                Object.keys(partForm.form.controls).forEach(key => {
                                    const control = partForm.form.get(key);
                                    if (control?.invalid) {
                                        const fieldName = `part_${concernIndex}_${correctionIndex}_${partIndex}_${key}`;
                                        invalidFields.push(fieldName);
                                        // Add display name with customer concern context
                                        const displayName = `Customer Concern: ${customerConcern} - Part ${this.getFieldDisplayName(key)}`;
                                        this.formValidationTracker.addFieldDisplayName(fieldName, displayName);
                                    }
                                });
                            }
                        });

                        // Check labor estimates
                        estimateList.laborForms?.forEach((laborForm, laborIndex) => {
                            if (laborForm?.form) {
                                Object.keys(laborForm.form.controls).forEach(key => {
                                    const control = laborForm.form.get(key);
                                    if (control?.invalid) {
                                        const fieldName = `labor_${concernIndex}_${correctionIndex}_${laborIndex}_${key}`;
                                        invalidFields.push(fieldName);
                                        // Add display name with customer concern context
                                        const displayName = `Customer Concern: ${customerConcern} - Labor ${this.getFieldDisplayName(key)}`;
                                        this.formValidationTracker.addFieldDisplayName(fieldName, displayName);
                                    }
                                });
                            }
                        });

                        // Check material estimates
                        estimateList.materialForms?.forEach((materialForm, materialIndex) => {
                            if (materialForm?.form) {
                                Object.keys(materialForm.form.controls).forEach(key => {
                                    const control = materialForm.form.get(key);
                                    if (control?.invalid) {
                                        const fieldName = `material_${concernIndex}_${correctionIndex}_${materialIndex}_${key}`;
                                        invalidFields.push(fieldName);
                                        // Add display name with customer concern context
                                        const displayName = `Customer Concern: ${customerConcern} - Supply ${this.getFieldDisplayName(key)}`;
                                        this.formValidationTracker.addFieldDisplayName(fieldName, displayName);
                                    }
                                });
                            }
                        });
                    });
                }
            });
        }
    }

    /**
     * Get the customer concern text for display purposes
     */
    private getCustomerConcernText(concernForm: any, concernIndex: number): string {
        try {
            // Try to get the concern from the form
            const concernText = concernForm?.form?.get('concern')?.value;
            if (concernText && concernText.trim().length > 0) {
                // Truncate long concerns for display
                return concernText.length > 50 ? concernText.substring(0, 50) + '...' : concernText;
            }
        } catch (error) {
            console.warn('Error getting customer concern text:', error);
        }

        // Fallback to generic naming
        return `Concern ${concernIndex + 1}`;
    }

    /**
     * Get user-friendly display name for a field key
     */
    private getFieldDisplayName(key: string): string {
        const fieldDisplayNames: { [key: string]: string } = {
            'component': 'Component',
            'partNumber': 'Part Number',
            'etaDays': 'Part ETA',
            'price': 'Price',
            'quantity': 'Quantity',
            'laborOperation': 'Labor Operation',
            'rate': 'Labor Rate',
            'hours': 'Hours',
            'description': 'Description',
            'isOem': 'OEM'
        };

        return fieldDisplayNames[key] || key;
    }

    /**
     *
     */
    private saveClaim(request: ClaimsAppRequest, estimateFile: File) {
        if (request === null) {
            this.showDialog('Error with Request',
                'There was an error submitting the claim. Please try again.',
                'Ok',
                undefined,
                () => { },
                () => { });
            return;
        }

        // If the form is already submitting, don't start a new request.
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        this.updateIsLoading(); // Update loading state when submitting starts

        // Console dump of what we're sending to the API
        console.log('=== CLAIM SUBMISSION DATA ===');
        console.log('Estimate File:', estimateFile);
        console.log('Claims Request:', JSON.stringify(request, null, 2));
        console.log('===========================');

        this.claimServiceSubscription = this.claimService.saveClaim(estimateFile, request)
            .subscribe({
                next: response => {
                    this.claimServiceResponseMessage = response?.message ?? this.defaultSuccessMessage;
                    this.isFormSubmitted = true;
                    this.isSubmitting = false;
                    this.customerSubmissionClicked = false;
                    this.updateIsLoading(); // Update loading state when submitting ends
                },
                error: response => {
                    if (response?.error?.message) {
                        this.claimServiceResponseMessage = response?.error?.message ?? this.defaultSuccessMessage;
                        this.isFormSubmitted = true;
                        this.customerSubmissionClicked = false;
                    }
                    else {
                        // Assumes ProblemDetails.
                        this.showDialog(response?.error?.title ?? 'Error with Request',
                            response?.error?.detail ?? 'There was an error submitting the claim. Please try again.',
                            'Ok',
                            undefined,
                            () => { },
                            () => { });
                    }
                    this.isSubmitting = false;                    
                    this.updateIsLoading(); // Update loading state when submitting ends with error
                }
            });
    }

    /**
     *
     */
    private buildRequest(): ClaimsAppRequest {
        const claimRequest = new ClaimsAppRequest();

        ClaimsAppSubmissionMapper.mapCustomerForm(claimRequest, this.customer.form);
        ClaimsAppSubmissionMapper.mapMechanicalBreakdownForm(claimRequest, this.mechanicalBreakdown.form);
        ClaimsAppSubmissionMapper.mapRepairFacilityInterface(claimRequest, this.repairFacility);
        ClaimsAppSubmissionMapper.mapContact(claimRequest, this.contact);

        ClaimsAppSubmissionMapper.mapConcernCausePartList(claimRequest, this.concernCausePartList);

        return claimRequest;
    }

    // Add property to control form visibility for reset
    public showForms = true;

    /**
     * Resets the form to its initial state.
     * @see {@link FormUtility.reset}
     */
    public reset(): void {
        // Reset component state first
        this.isFormSubmitted = false;
        this.shouldScrollToTop = true;
        this.customerValidated = false;
        this.breakdownValidated = false;
        this.customerSubmissionClicked = false;

        // Clear invalid fields
        this.formValidationTracker.clearInvalidFields();

        // Clear files
        this.estimateAttachment?.clearFiles();

        // Clear labor rate service (lowest value tracking)
        this.laborRateService?.clearRates();
        console.log('Labor rates cleared');

        // Temporarily hide forms to force complete re-render
        this.showForms = false;
        this.changeDetectorRef.detectChanges();

        setTimeout(() => {
            // Show forms again
            this.showForms = true;
            this.changeDetectorRef.detectChanges();

            // Wait another tick for forms to be re-rendered, then reset
            setTimeout(() => {
                // Reset customer form
                if (this.customer?.form) {
                    // Force complete reset
                    this.customer.form.reset({
                        customerFirstName: '',
                        customerLastName: '',
                        contractNumber: '',
                        vin: ''
                    });
                    this.customer.formCompleted = false;
                }

                // Reset mechanical breakdown form
                if (this.mechanicalBreakdown?.form) {
                    this.mechanicalBreakdown.form.reset({
                        breakdownDate: '',
                        currentOdometer: null,
                        transportationMode: null,
                        isCollision: false,
                        hasModification: false,
                        IsCommercialUse: false,
                        repairOrderNumber: ''
                    });
                    this.mechanicalBreakdown.formCompleted = false;
                    // Reset to initial state - hasTransportationMode starts as true
                    this.mechanicalBreakdown.hasTransportationMode = true;
                }

                // Reset concern cause part list
                if (this.concernCausePartList) {
                    this.concernCausePartList.reset();
                }

                // Final change detection and progress update
                this.changeDetectorRef.detectChanges();
                this.updateProgress();

                // Re-establish form subscriptions after component recreation
                this.setupFormSubscriptions();

                console.log('Reset completed - customer form:', this.customer?.form?.value);
                console.log('Reset completed - mechanical form:', this.mechanicalBreakdown?.form?.value);
            }, 50);
        }, 50);
    }

    /**
     * Set up form subscriptions for validation tracking
     */
    private setupFormSubscriptions(): void {
        setTimeout(() => {
            // Check if component is still alive before setting up subscriptions
            if (this._destroying$.closed) {
                return;
            }

            if (this.customer?.form) {
                this.customer.form.valueChanges
                    .pipe(takeUntil(this._destroying$))
                    .subscribe(() => {
                        setTimeout(() => {
                            this.updateProgress();
                            this.updateValidationState();
                            this.changeDetectorRef.markForCheck();
                        }, 0);
                    });
            }
            if (this.mechanicalBreakdown?.form) {
                this.mechanicalBreakdown.form.valueChanges
                    .pipe(takeUntil(this._destroying$))
                    .subscribe(() => {
                        setTimeout(() => {
                            this.updateProgress();
                            this.updateValidationState();
                            this.changeDetectorRef.markForCheck();
                        }, 0);
                    });
            }
            if (this.concernCausePartList?.objectForms) {
                // Monitor changes in concern cause part list forms
                this.concernCausePartList.objectForms.changes
                    .pipe(takeUntil(this._destroying$))
                    .subscribe(() => {
                        this.subscribeToObjectFormsChanges();
                    });
                // Subscribe to initial forms
                this.subscribeToObjectFormsChanges();
            }

            // Set up a recurring check to establish estimate form subscriptions
            // This is needed because estimate line items may be created dynamically after reset
            this.setupEstimateFormSubscriptionMonitoring();
        }, 100);
    }

    /**
     * Set up monitoring to establish estimate form subscriptions as they become available
     */
    private setupEstimateFormSubscriptionMonitoring(): void {
        const checkAndSetupEstimateSubscriptions = () => {
            if (this._destroying$.closed) {
                return;
            }

            try {
                if (this.concernCausePartList?.objectForms) {
                    this.concernCausePartList.objectForms.forEach((concernForm) => {
                        if (concernForm?.correctionEstimateList?.objectForms) {
                            // Set up estimate form subscriptions for any forms that exist
                            this.subscribeToEstimateFormChanges(concernForm);
                        }
                    });
                }
            } catch (error) {
                console.warn('Error in estimate form subscription monitoring:', error);
            }

            // Check again in case new forms are added later
            setTimeout(checkAndSetupEstimateSubscriptions, 200);
        };

        // Start monitoring after a delay to allow forms to be created
        setTimeout(checkAndSetupEstimateSubscriptions, 300);
    }

    /**
     * Update the progress indicator based on form completion and repair facility selection.
     */
    private updateProgress(): void {
        const customerValid = this.customer?.form?.valid;
        const breakdownValid = this.mechanicalBreakdown?.form?.valid;
        const repairFacilityComplete = !!this.repairFacility;

        // Determine error state for each section
        const customerError = this.customerValidated && !customerValid;
        const breakdownError = this.breakdownValidated && !breakdownValid;

        // TODO: Move the colors to a style sheet for easier maintenance.
        // TODO: Move this toggling logic to the progress step component. Should be handled internally and not manually by consumer.
        const steps: ProgressStep[] = [
            { label: 'Customer', status: customerValid ? 'complete' : (customerError ? 'error' : 'active'), color: customerValid ? '#8bc34a' : (customerError ? '#e57373' : '#ffeb3b'), sectionId: 'form-nav-customer' },
            { label: 'Mechanical Breakdown', status: 'upcoming', color: '#fff', sectionId: 'form-nav-mechanical-breakdown' },
            { label: 'Estimate Intake', status: 'upcoming', color: '#fff', sectionId: 'form-nav-concern-entry' },
            { label: 'Submit', status: 'upcoming', color: '#fff', sectionId: 'form-nav-submit' }
        ];

        let activeIdx = 0;
        if (customerValid || customerError) {
            steps[0].status = customerValid ? 'complete' : (customerError ? 'error' : 'active');
            steps[0].color = customerValid ? '#8bc34a' : (customerError ? '#e57373' : '#ffeb3b');
            steps[1].status = breakdownValid ? 'complete' : (breakdownError ? 'error' : 'active');
            steps[1].color = breakdownValid ? '#8bc34a' : (breakdownError ? '#e57373' : '#ffeb3b');
            activeIdx = 1;
        }
        if ((customerValid || customerError) && (breakdownValid || breakdownError)) {
            steps[1].status = breakdownValid ? 'complete' : (breakdownError ? 'error' : 'active');
            steps[1].color = breakdownValid ? '#8bc34a' : (breakdownError ? '#e57373' : '#ffeb3b');
            steps[2].status = repairFacilityComplete ? 'complete' : 'active';
            steps[2].color = repairFacilityComplete ? '#8bc34a' : '#ffeb3b';
            activeIdx = 2;
        }
        if ((customerValid || customerError) && (breakdownValid || breakdownError) && repairFacilityComplete) {
            steps[2].status = repairFacilityComplete ? 'complete' : 'active';
            steps[2].color = repairFacilityComplete ? '#8bc34a' : '#ffeb3b';
            steps[3].status = 'active';
            steps[3].color = '#ffeb3b';
            activeIdx = 3;
        }
        this.progressSteps = steps;
        this.activeStepIndex = activeIdx;
        this.changeDetectorRef.detectChanges();
    }

    onSectionSubmitted(sectionName: string) {
        switch (sectionName) {
            case 'customer':
                this.customerSubmissionClicked = true;
                this.customerValidated = this.validateForm([], this.customer);
                this.changeDetectorRef.detectChanges();
                this.scrollToSection('form-nav-mechanical-breakdown');
                break;
        }
    }
}
